import React, { useRef, useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { FaPlus, FaArrowLeft } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { addReels, updateReels, listReels } from "../../Redux/Actions/projectActions";
import toast from "react-hot-toast";
import "./addVideo.css";
import {
    completeMultipartUpload,
    generatePreSignedUrls,
    s3MultipartUploadStart,
    s3Upload,
    splitFileIntoChunks,
} from "../../Utils/s3Upload.jsx";

const VideoModal = ({ projectId: propProjectId, onClose }) => {
    const dispatch = useDispatch();
    const { project, reelsLoading, reelsList } = useSelector((state) => state.projects || {});

    const [mode, setMode] = useState("view");
    const [builderVideos, setBuilderVideos] = useState({});
    const [selectedBusinessType, setSelectedBusinessType] = useState("fresh");
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [playingIndex, setPlayingIndex] = useState(null);
    const [editingReelId, setEditingReelId] = useState(null);
    const [uploadedFileName, setUploadedFileName] = useState("");
    const [capturing, setCapturing] = useState(false); // NEW: capture loading state

    const imageInputRef = useRef(null);
    const videoPreviewRef = useRef(null);

    const projectId = propProjectId || project?.data?._id;
    const reels = reelsList?.data || [];
    const businessTypes = ["fresh", "resale", "business", "rental"];

    const currentStep = !builderVideos.video ? 1 : !builderVideos.thumbnail ? 2 : 3;

    useEffect(() => {
        if (mode === "view" && projectId) {
            dispatch(listReels({ page: 1, limit: 100, project: projectId }));
        }
    }, [mode, dispatch, projectId]);

    const handleBack = () => {
        if (mode === "view") {
            onClose();
        } else {
            setMode("view");
            setBuilderVideos({});
            setEditingReelId(null);
            setSelectedBusinessType("fresh");
            setUploadedFileName("");
        }
    };

    const handleScroll = (e) => { e.stopPropagation(); };

    const handleVideoInput = async (e) => {
        const file = e.target.files[0];
        if (!file) { toast.error("No file selected"); return; }

        const sanitizedFileName = file.name.replace(/\s+/g, "_");
        const videoElement = document.createElement("video");
        videoElement.preload = "metadata";

        videoElement.onloadedmetadata = async () => {
            window.URL.revokeObjectURL(videoElement.src);
            const duration = videoElement.duration;

            if (file.size > 10 * 1024 * 1024) {
                toast.error("Video size must be 10 MB or less.");
                return;
            }
            if (duration > 300) {
                toast.error("Only videos up to 5 minutes are allowed.");
                return;
            }

            try {
                setUploading(true);
                setUploadProgress(0);
                setUploadedFileName(file.name);

                const key = sanitizedFileName;
                const uploadResult = await s3MultipartUploadStart(key);
                const uploadId = uploadResult?.data;
                const chunks = splitFileIntoChunks(file);
                const chunkCount = chunks.length;
                const presignedUrls = await generatePreSignedUrls(key, uploadId, chunkCount);
                const urls = presignedUrls?.data;

                const parts = [];
                for (let i = 0; i < chunkCount; i++) {
                    const uploadRes = await fetch(urls[i], { method: "PUT", body: chunks[i] });
                    if (!uploadRes.ok) toast.error(`Failed to upload chunk ${i + 1}`);
                    const etag = uploadRes.headers.get("ETag");
                    const rawEtag = etag.replace(/^"(.+)"$/, "$1");
                    parts.push({ ETag: rawEtag, PartNumber: i + 1 });
                    setUploadProgress(Math.round(((i + 1) / chunkCount) * 100));
                }

                await completeMultipartUpload(key, uploadId, parts);
                const fileUrl = urls[0].split("?")[0];
                setBuilderVideos({ video: fileUrl, key, thumbnail: "" });
            } catch {
                toast.error("Failed to upload video.");
            } finally {
                setUploading(false);
                setUploadProgress(0);
            }
        };

        videoElement.onerror = () => { toast.error("Failed to read video metadata."); };
        videoElement.src = URL.createObjectURL(file);
    };

    const handleCaptureFrame = async () => {
        const video = videoPreviewRef.current;
        if (!video) return;

        try {
            setCapturing(true); // show loading
            const currentTime = video.currentTime;
            const response = await fetch(video.src, { mode: "cors" });
            const blob = await response.blob();
            const localUrl = URL.createObjectURL(blob);

            const tempVideo = document.createElement("video");
            tempVideo.crossOrigin = "anonymous";
            tempVideo.preload = "auto";
            tempVideo.src = localUrl;

            tempVideo.onloadedmetadata = () => {
                tempVideo.currentTime = currentTime;
            };

            tempVideo.onseeked = async () => {
                const canvas = document.createElement("canvas");
                canvas.width = tempVideo.videoWidth;
                canvas.height = tempVideo.videoHeight;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(tempVideo, 0, 0, canvas.width, canvas.height);
                URL.revokeObjectURL(localUrl);

                canvas.toBlob(async (capturedBlob) => {
                    if (!capturedBlob) {
                        toast.error("Failed to capture frame");
                        setCapturing(false);
                        return;
                    }
                    const file = new File([capturedBlob], "thumbnail.jpg", { type: "image/jpeg" });
                    const s3Link = await s3Upload(file, dispatch);
                    setCapturing(false); // hide loading
                    if (!s3Link) { toast.error("Failed to upload thumbnail"); return; }
                    setBuilderVideos((prev) => ({ ...prev, thumbnail: s3Link }));
                    toast.success("Thumbnail captured!");
                }, "image/jpeg", 0.92);
            };

            tempVideo.onerror = () => {
                URL.revokeObjectURL(localUrl);
                setCapturing(false);
                toast.error("Failed to load video for frame capture");
            };

            tempVideo.load();
        } catch (err) {
            setCapturing(false);
            toast.error("Failed to capture frame. Check S3 CORS settings.");
            console.error(err);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        const payload = {
            ...(editingReelId ? { id: editingReelId } : { project: projectId }),
            business_type: selectedBusinessType,
            videos: [{
                video: builderVideos.video || "",
                key: builderVideos.key || (builderVideos.video ? builderVideos.video.split("/").pop() : ""),
                thumbnail: builderVideos.thumbnail || "",
            }],
        };
        const result = await dispatch(editingReelId ? updateReels(payload) : addReels(payload));
        if (result) {
            setMode("view");
            setBuilderVideos({});
            setEditingReelId(null);
            setSelectedBusinessType("fresh");
            setUploadedFileName("");
            dispatch(listReels({ page: 1, limit: 100, project: projectId }));
        }
    };

    const handleDeleteReel = async (reelId) => {
        const result = await dispatch(updateReels({ id: reelId, videos: [] }));
        if (result) dispatch(listReels({ page: 1, limit: 100, project: projectId }));
    };

    const EditBody = () => (
        <div className="vm-edit-body">

            {/* STEP 1 — upload zone */}
            {currentStep === 1 && !uploading && (
                <div className="vm-upload-zone" onClick={() => imageInputRef.current.click()}>
                    <div className="vm-upload-icon">☁️</div>
                    <div>
                        <p className="vm-upload-title">Drop your video here</p>
                        <p className="vm-upload-sub">or click to browse from your device</p>
                    </div>
                    <div className="vm-upload-badges">
                        <span className="vm-badge">MP4</span>
                        <span className="vm-badge">MOV</span>
                        <span className="vm-badge-muted">· Max 10MB · 5 min</span>
                    </div>
                    <button
                        className="vm-btn-dark"
                        onClick={(e) => { e.stopPropagation(); imageInputRef.current.click(); }}
                    >Choose File</button>
                </div>
            )}

            {/* STEP 1 — uploading progress */}
            {uploading && (
                <div className="vm-upload-progress-box">
                    <div className="vm-upload-progress-icon">⬆️</div>
                    <div>
                        <p className="vm-upload-progress-label">Uploading video...</p>
                        <p className="vm-upload-progress-pct">{uploadProgress}%</p>
                    </div>
                    <div className="vm-progress-track">
                        <div className="vm-progress-fill" style={{ width: `${uploadProgress}%` }} />
                    </div>
                    <p className="vm-upload-progress-note">Please don't close this window</p>
                </div>
            )}

            {/* STEP 1 done — green success bar */}
            {builderVideos.video && (
                <div className="vm-success-bar">
                    <div className="vm-success-icon">✓</div>
                    <div className="vm-success-text">
                        <p className="vm-success-title">Video uploaded</p>
                        <p className="vm-success-filename">
                            {uploadedFileName || builderVideos.video.split("/").pop()}
                        </p>
                    </div>
                    <button
                        className="vm-btn-ghost"
                        onClick={() => { setBuilderVideos({}); setUploadedFileName(""); imageInputRef.current.click(); }}
                        disabled={reelsLoading || uploading}
                    >Replace</button>
                </div>
            )}

            {/* STEP 2 — video player with capture button */}
            {builderVideos.video && (
                <div className="vm-video-section">
                    <div className="vm-video-wrapper">
                        <video
                            ref={videoPreviewRef}
                            crossOrigin="anonymous"
                            src={builderVideos.video}
                            controls
                            className="vm-video-player"
                        />
                        {/* Capture button — shows spinner when capturing */}
                        <div className="vm-capture-btn-wrap">
                            <button
                                onClick={handleCaptureFrame}
                                disabled={reelsLoading || uploading || capturing}
                                className={`vm-capture-btn${capturing ? " vm-capture-btn--loading" : ""}`}
                            >
                                {capturing ? (
                                    <>
                                        <span className="vm-spinner" />
                                        Capturing…
                                    </>
                                ) : (
                                    <>
                                        📸 {builderVideos.thumbnail ? "Recapture frame" : "Capture this frame"}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                    <p className="vm-capture-hint">
                        Pause at any moment → tap <strong>Capture this frame</strong>
                    </p>
                </div>
            )}

            {/* STEP 2 — thumbnail captured */}
            {builderVideos.thumbnail && (
                <div className="vm-thumb-bar">
                    <img src={builderVideos.thumbnail} alt="thumbnail" className="vm-thumb-preview" />
                    <div className="vm-thumb-text">
                        <p className="vm-thumb-title">Thumbnail captured</p>
                        <p className="vm-thumb-sub">Tap recapture to change it</p>
                    </div>
                    <button
                        className="vm-btn-ghost"
                        onClick={() => setBuilderVideos((prev) => ({ ...prev, thumbnail: "" }))}
                        disabled={reelsLoading || uploading}
                    >Remove</button>
                </div>
            )}

            {/* STEP 3 — business type */}
            {!builderVideos.thumbnail ? (
                <div className="vm-locked-row">
                    <div className="vm-locked-icon">🔒</div>
                    <div>
                        <p className="vm-locked-title">Business Type</p>
                        <p className="vm-locked-sub">Unlocks after thumbnail is captured</p>
                    </div>
                    <span className="vm-step-badge">Step 3</span>
                </div>
            ) : (
                <div className="vm-business-section">
                    <label className="vm-label">Business type</label>
                    <div className="vm-business-grid">
                        {businessTypes.map((type) => (
                            <button
                                key={type}
                                onClick={() => setSelectedBusinessType(type)}
                                disabled={reelsLoading || uploading}
                                className={`vm-type-btn${selectedBusinessType === type ? " vm-type-btn--active" : ""}`}
                            >{type}</button>
                        ))}
                    </div>
                </div>
            )}

            {/* ACTION BUTTONS */}
            <div className="vm-actions">
                <button
                    onClick={handleBack}
                    disabled={reelsLoading || uploading}
                    className="vm-btn-cancel"
                >Cancel</button>
                <button
                    onClick={handleSave}
                    disabled={reelsLoading || uploading || !builderVideos.video || !builderVideos.thumbnail}
                    className={`vm-btn-save${(reelsLoading || uploading || !builderVideos.video || !builderVideos.thumbnail) ? " vm-btn-save--disabled" : ""}`}
                >
                    ☁️ {reelsLoading ? "Saving..." : editingReelId ? "Update Reel" : "Save Reel"}
                </button>
            </div>
        </div>
    );

    return ReactDOM.createPortal(
        <div className="vm-overlay" onClick={onClose}>
            <div className="vm-modal" onClick={(e) => e.stopPropagation()} onWheel={handleScroll}>

                {/* HEADER */}
                <div className="vm-header">
                    <div className="vm-header-top">
                        <div className="vm-header-left">
                            <div className="vm-header-icon">🎬</div>
                            <div>
                                <p className="vm-header-title">Project Reels</p>
                                <p className="vm-header-sub">
                                    {mode === "view"
                                        ? "Manage your reels collection"
                                        : editingReelId
                                            ? "Update reel · Edit video & thumbnail"
                                            : "Upload video · Pick thumbnail · Save"}
                                </p>
                            </div>
                        </div>
                        <div className="vm-header-right">
                            {mode === "edit" && (
                                <button
                                    onClick={handleBack}
                                    disabled={reelsLoading || uploading}
                                    className="vm-btn-back"
                                >
                                    <FaArrowLeft size={10} /> Back
                                </button>
                            )}
                            {mode === "view" && (
                                <button
                                    disabled={reelsLoading || uploading}
                                    onClick={() => setMode("edit")}
                                    className="vm-btn-add"
                                >
                                    <FaPlus size={10} /> Add New Reel
                                </button>
                            )}
                            <button onClick={onClose} className="vm-btn-close">×</button>
                        </div>
                    </div>

                    {/* Step indicator */}
                    {mode === "edit" && (
                        <div className="vm-steps">
                            {[{ n: 1, label: "Video" }, { n: 2, label: "Thumbnail" }, { n: 3, label: "Details" }].map((s, i, arr) => (
                                <React.Fragment key={s.n}>
                                    <div className="vm-step-item">
                                        <div className={`vm-step-circle${currentStep > s.n ? " vm-step-circle--done" : currentStep === s.n ? " vm-step-circle--active" : ""}`}>
                                            {currentStep > s.n ? "✓" : s.n}
                                        </div>
                                        <span className={`vm-step-label${currentStep === s.n ? " vm-step-label--active" : currentStep > s.n ? " vm-step-label--done" : ""}`}>
                                            {s.label}
                                        </span>
                                    </div>
                                    {i < arr.length - 1 && (
                                        <div className={`vm-step-line${currentStep > s.n ? " vm-step-line--done" : ""}`} />
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    )}
                </div>

                {/* BODY */}
                <div className="vm-body">
                    <input
                        type="file" accept="video/*"
                        ref={imageInputRef}
                        onChange={handleVideoInput}
                        hidden
                        disabled={reelsLoading || uploading}
                    />

                    {mode === "edit" ? (
                        <EditBody />
                    ) : (
                        <div className="vm-grid">
                            {reels?.length ? (
                                reels.map((reel, reelIndex) =>
                                    reel?.videos?.map((video, videoIndex) => (
                                        <div key={`${reelIndex}-${videoIndex}`} className="vm-reel-card">
                                            <div className="vm-reel-media">
                                                {playingIndex === `${reelIndex}-${videoIndex}` ? (
                                                    <video
                                                        src={video.video}
                                                        className="vm-reel-video"
                                                        controls autoPlay
                                                        onEnded={() => setPlayingIndex(null)}
                                                    />
                                                ) : (
                                                    <>
                                                        <img src={video.thumbnail} alt="thumbnail" className="vm-reel-thumb" />
                                                        <button
                                                            onClick={() => setPlayingIndex(`${reelIndex}-${videoIndex}`)}
                                                            className="vm-play-btn"
                                                        >▶</button>
                                                    </>
                                                )}
                                                <div className="vm-reel-actions">
                                                    <button
                                                        onClick={() => {
                                                            setEditingReelId(reel._id);
                                                            setBuilderVideos({ video: video.video, key: video.key, thumbnail: video.thumbnail });
                                                            setUploadedFileName(video.video.split("/").pop());
                                                            setSelectedBusinessType(reel.business_type || "fresh");
                                                            setMode("edit");
                                                        }}
                                                        disabled={reelsLoading || uploading}
                                                        className="vm-reel-action-btn"
                                                    >✎</button>
                                                    <button
                                                        onClick={() => handleDeleteReel(reel._id)}
                                                        disabled={reelsLoading || uploading}
                                                        className="vm-reel-action-btn"
                                                    >×</button>
                                                </div>
                                                {reel.business_type && (
                                                    <div className="vm-reel-type-badge">
                                                        {reel.business_type}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))
                                )
                            ) : (
                                <div className="vm-empty">
                                    <p className="vm-empty-icon">🎬</p>
                                    <p className="vm-empty-title">No reels yet</p>
                                    <p className="vm-empty-sub">Add your first reel to get started</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default VideoModal;